import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Praefix des Query-Keys dieses Hooks. Exportiert, damit die Schreibpfade des
 * Ads-Panels (`useAdsAccountActions` in `useAdsPerformance.ts`) die Teaser-
 * Kontozeile nach Connect/Disconnect ueber ALLE MCs invalidieren koennen, ohne
 * die 30-min-staleTime abzuwarten (Review-Ledger-Minor, Report V2 H1).
 */
export const LINKED_ADS_ACCOUNT_QUERY_KEY = 'linked-ads-account' as const;

/**
 * Erkennt das mit der aktuellen Merchant Center verknuepfte Ads-Konto —
 * aus der EIGENEN Tabelle google_ads_accounts (RLS-gescoped), ohne jeden
 * Google-Call und ohne adwords-Scope. Zeilen existieren nur, wenn frueher
 * ein Connect/Ingest lief; fuer die meisten Nutzer ist das Ergebnis null
 * und die Konto-Zeile im Teaser bleibt einfach weg.
 *
 * SPALTEN EINZELN NENNEN: die Tabelle traegt OAuth-Credentials und hat nur
 * ein spaltengebundenes SELECT-Grant — select('*') liefert 42501 (SCHEMA.md).
 */
export interface LinkedAdsAccount {
  /** google_ads_accounts.id, der zweite Teil des Schluessels von ads_feed_summaries. */
  id: string;
  adsCustomerId: string;
}

export function useLinkedAdsAccount(mcRowId: string | null) {
  return useQuery({
    queryKey: [LINKED_ADS_ACCOUNT_QUERY_KEY, mcRowId],
    enabled: !!mcRowId,
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<LinkedAdsAccount | null> => {
      const { data, error } = await supabase
        .from('google_ads_accounts')
        // Spalten einzeln: spaltengebundenes SELECT-Grant (SCHEMA.md). `id`
        // steht darin (Migration 20260802120000), es braucht keine Migration.
        .select('id, ads_customer_id, linked_merchant_center_id')
        .eq('linked_merchant_center_id', mcRowId as string)
        // VERKNUEPFT REICHT NICHT, ES MUSS AUCH AKTIV SEIN.
        //
        // `handleAdsConnect` schreibt `linked_merchant_center_id` auf JEDE
        // Ads-Zeile des Grants, sobald die Merchant-API-Aufloesung durch ist,
        // aktiviert aber hoechstens eine (`preselectAdsAccount` nur bei genau
        // einem verknuepften Nicht-MCC-Konto). Ein Grant mit mehreren
        // verknuepften Konten -- Agentur, Kunde mit MCC-Kindern -- hat danach
        // lauter Zeilen mit Verknuepfung und `is_active = false`.
        //
        // Gezogen wird aber ausschliesslich aktiv: der Nachtlauf waehlt seine
        // Konten ueber `is_active`, und `ads-first-pull` ebenso. Ohne diesen
        // Filter meldete das Kapitel "Verbunden, die Zahlen kommen spaetestens
        // morgen frueh" fuer ein Konto, das nie gezogen wird -- dauerhaft, und
        // ohne Weg zurueck, weil der Zustand dann nicht `unlinked` ist und
        // "Konto zuordnen" gar nicht erst angeboten wird. Mit dem Filter faellt
        // so eine Zeile auf `unlinked`, der Dialog zeigt sie (gleiche MC, also
        // waehlbar), `link_google_ads_account` setzt `is_active = true`.
        //
        // Derselbe Filter traegt den Rueckweg nach "Trennen":
        // `disconnect_google_ads_account` nullt die Tokens und setzt
        // `is_active = false`, laesst die Verknuepfung aber stehen.
        .eq('is_active', true)
        // Determinismus bei mehreren aktiven, verknuepften Zeilen: die juengste
        // Verbindung gewinnt. `is_active` bleibt als erster Sortierschluessel
        // stehen, obwohl der Filter darueber nur noch aktive Zeilen durchlaesst
        // -- er kostet nichts und haelt die Reihenfolge stabil, falls der
        // Filter je fallen sollte. Beide Spalten stehen im spaltengebundenen
        // SELECT-Grant der Tabelle (SCHEMA.md).
        .order('is_active', { ascending: false })
        .order('connected_at', { ascending: false })
        .limit(1);
      if (error) {
        // Non-fatal fuer den Teaser (er rendert ohne Konto-Zeile), aber NICHT
        // stumm: ein `return null` allein schluckte auch 42501 (Grant-Entzug)
        // — exakt die dokumentierte Falle "Rechte-Entzug auf einem Pfad ohne
        // Fehlerauswertung" (SCHEMA.md). Best-effort nach Sentry, dann null.
        Sentry.captureException(error, {
          tags: { feature: 'ads-performance', hook: 'useLinkedAdsAccount' },
        });
        return null;
      }
      const row = data?.[0] as { id?: string; ads_customer_id?: string } | undefined;
      return row?.id && row.ads_customer_id ? { id: row.id, adsCustomerId: row.ads_customer_id } : null;
    },
  });
}
